import React, {useEffect, useState} from 'react';

import './Programs.scss';
import ProgramBasePatient from './Programs.patient';
import {useLazyQuery} from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';
import KitAssignDialog from '../kitAssign/kit.assign.dialog';
import LocalStorageUtil from "../services/localStorageUtil";

import { Subject } from 'rxjs';
import ButtonIcon from '../forms/buttons/Button.icon';
import {Link, useParams} from 'react-router-dom';
import ProgramsBaseEnrollPatientDialog from './Programs.patient.enroll';
import cacheStorage from '../services/cacheStorage';
import SpinnerLoader from "../visuals/SpinnerLoader";
import {useTranslation} from "react-i18next";
import Checkbox from "../forms/inputs/Checkboxk";
import ItemMenu from "../forms/menu/MenuComponent";
import ParticipantCliniciansDialog from "./dialogs/Participant.clinicians.dialog";
import ProgramGenerateZipReportsDialog from "./dialogs/Program.generate.zip.report.dialog";
import ProgramDownloadZipReportsDialog from "./dialogs/Program.download.zip.report.dialog";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";

function ProgramsBaseEnrolled({ currentOrg, match, currentUser, toaster, program, params }) {
    let {programId} = useParams();
    const {t} = useTranslation(['programs','common','users','devices','teams']);
    const [data, setData] = useState({ getUserPatients: [] });
    const [loadData, setLoadData] = useState(true);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [assigneePatient, setAssigneePatient] = useState(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('name');
    const [newsOnTop, setNewsOnTop] = useState(true);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [requestEnroll, setRequestEnroll] = useState(false);
    const [openParticipantsCliniciansModal, setOpenParticipantsCliniciansModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    const [openZipModal, setOpenZipModal] = useState(false);
    const [multipleReports, setMultipleReports] = useState(false);
    const [patientsBioDevices, setPatientsBioDevices] = useState([]);
    const [openDownloadModal, setOpenDownloadModal] = useState(false);
    const [reportLink, setReportLink] = useState(null);

    const [fetchPersonByProgram] = useLazyQuery(gqlSchemaLibrary.patients.getPersonByProgram, {
        onCompleted: (onPatients) => {
            const patientsByProgram = onPatients.getPersonByProgram.person.map(p => ({...p}));
            if (patientsByProgram) {
                const newPatientsString = cacheStorage.getItem(`${currentUser.selectedOrgId}_programPatients`);
                if(newPatientsString && newPatientsString.value){
                    const newPatientsIds = JSON.parse(newPatientsString.value);
                    patientsByProgram.forEach((patient) => patient.isNew = newPatientsIds.some(id => id === patient._id) && !patient.kitId);
                }
                setData({ getUserPatients: patientsByProgram });
                onSelect(patientsByProgram[0] || null);

                fetchPersonDeviceByType({
                    variables: {
                        patientIds: patientsByProgram.map((p) => p._id),
                        deviceType: ['BioButton', 'BioSticker', 'Rechargeable BioButton']
                    }
                });
            }
            setLoadData(false);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [fetchPersonDeviceByType] = useLazyQuery(gqlSchemaLibrary.patients.getPatientsDevicesByType, {
        onCompleted: (onData) => {
            setPatientsBioDevices(onData.getPatientsDevicesByType);
        },
        fetchPolicy: 'cache-and-network'
    });

    useEffect(() => {
        fetchPersonByProgram({
            variables: {
                programId: programId
            }
        });
        if (currentUser.clinician.role === 'clinician' || currentUser.clinician.role === 'admin') {
            const ps = LocalStorageUtil.getItem('params');
            if (ps) {
                const data = JSON.parse(ps);
                if (data && data.length > 1) {
                    setReportLink(data[1]);
                    setOpenDownloadModal(true);
                }
            }
        } else {
            LocalStorageUtil.removeItem('params');
        }
    }, [currentUser, fetchPersonByProgram])

    const onSelect = (patient) => {
        if(!currentUser.userRoles.devicesAssign.edit){
            setSelectedPatient({});
            return;
        }
        setSelectedPatient(patient);
    }

    const assignKitToSelected = (patient) => () => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'onClick', action: 'click option add kit to patient', patient_id: patient._id});
        setAssigneePatient(patient);
    }

    const closeAssignKit = (kitAssigned = null) => {
        if(!kitAssigned){
            setAssigneePatient(null);
        }else{
            setAssigneePatient(null);
            fetchPersonByProgram({
                variables: {
                    programId: programId
                }
            })
        }

    }

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'input', event: 'onSearch', action: 'input search patients', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const enrollParticipant = () => {
        setRequestEnroll(true);
    }

    const closeEnrollModal = (hadEnrollment) => {
        if (!hadEnrollment) {
            setRequestEnroll(false);
        } else {
            // RE-Fetch patients in programm
            setRequestEnroll(false);
            console.log('[Programs.enrolled] Lazy Query')
            fetchPersonByProgram({
                variables: {
                    programId: programId
                }
            })
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const sortList = (key) => {
        return () => {
            setSorting(key);
            setSortAscending(!sortAscending);
            AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setNewsOnTop(false);
        }
    }

    const onSelectAll = () => {
        const status = !selectAll;
        if (status) {
            setSelectedParticipants(Object.assign([], data.getUserPatients));
        } else {
            setSelectedParticipants([]);
        }
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'checkbox', event: 'onCheck', action: 'button selected all participants', program_id: program._id, check: status});
        setSelectAll(status);
    }

    const onChange = (item) => {
        const _team = selectedParticipants.find((t) => t._id === item._id);
        const index = selectedParticipants.indexOf(_team);
        const checked = index > -1;
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'checkbox', event: 'onCheck', action: 'button selected participant', program_id: program._id, patient_id: item._id, check: !checked});
        if (checked) {
            selectedParticipants.splice(index, 1);
        } else {
            selectedParticipants.push(item);
        }
        if (!selectedParticipants.length) {
            setSelectAll(false);
        }
        setSelectedParticipants(Object.assign([], selectedParticipants));
    }

    const closeParticipantsCliniciansModal = (_items) => {
        setSelectedParticipant(null);
        setOpenParticipantsCliniciansModal(false);
    }

    const onAddParticipants = () => {
        if (selectedParticipants.length) {
            setOpenParticipantsCliniciansModal(true);
        }
    }

    const onAddParticipant = (p) => {
        AuditTrail.sendLog(PAGES.PROGRAM_PARTICIPANTS, {tag: 'button', event: 'onClick', action: 'click option assign or unassign teams or users', patient_id: p._id});
        setSelectedParticipant(p);
        setOpenParticipantsCliniciansModal(true);
    }

    const patientsFiltered = data.getUserPatients.filter(p => {
        if(!search){
            return true;
        }
        const firstName = (p.user.bio.firstname||'').toLowerCase();
        const lastName = (p.user.bio.lastname||'').toLowerCase();
        const searchWords = search.split(' ').map(w => w.toLowerCase());
        return searchWords.reduce((p,word) => {
            return p && (firstName.includes(word) || lastName.includes(word));
        }, true);
    }).sort( (a, b) => {
        if(!sorting){
            return true;
        }
        if(sorting === 'name'){
            const aName = (a.user.bio.lastname||'')+' '+(a.user.bio.firstname||'');
            const bName = (b.user.bio.lastname||'')+' '+(b.user.bio.firstname||'');
            if(sortAscending){
                return aName.localeCompare(bName);
            }else{
                return bName.localeCompare(aName);
            }
        }else{
            if(sortAscending){
                return (a.dateOfBirth > b.dateOfBirth)? 1: -1;
            }else{
                return (a.dateOfBirth > b.dateOfBirth)? -1: 1;
            }
        }
     })
    const oldPatientsFiltered = patientsFiltered.filter(p => {
        if(newsOnTop){
            return p && !p.isNew;
        }
        return true;
    });
    const newPatientsFiltered = patientsFiltered.filter(p => {
        if(newsOnTop){
            return p && p.isNew;
        }
        return false;
    });

    const countPatients = oldPatientsFiltered.length + newPatientsFiltered.length;

    const enableDownloadBioReports = () => {
        let hasBioDevices = false;
        const patientsIdsBioDevices = patientsBioDevices.map((p) => p._id);
        for (const p of selectedParticipants) {
            if (patientsIdsBioDevices.includes(p._id)) {
                hasBioDevices = true;
                break;
            }
        }
        if (!hasBioDevices) {
            return false;
        }
        return currentUser.clinician.role === 'clinician' || currentUser.clinician.role === 'admin';
    }

    const onDownloadBioReports = (multiple) => {
        if (!selectedParticipants.length && !selectedPatient) {
            return;
        }
        setMultipleReports(multiple);
        setOpenZipModal(true);
    }

    const onCloseGenerateZipModal = () => {
        setOpenZipModal(false);
    }

    const onCloseDownloadZipModal = () => {
        setOpenDownloadModal(false);
    }

    const renderProgramEnrolled = () => {
        return (
            <div id="ProgramsBase">
                <div className="filter">
                    <div className="programOptions">
                        <i className="material-icons">
                            <Link to="/programs">
                                arrow_back_ios
                        </Link>
                        </i>
                        <div className="progTitle">
                            <h3>{!!program && program.name ? program.name : ''}</h3>
                            <div className="_active">
                                {t('active')}
                        </div>
                        </div>
                    </div>
                    <div className={`programFilters ${currentUser.userRoles.programsEnroll.edit ? '' : '_col3'}`}>
                        <div className="searchFilters">
                            <div></div>
                            <div className="searchContainer">
                                <div className={`search ${searchFocused ? 'focused' : ''}`}>
                                    <input value={search} placeholder={t('common:search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                                    <i className="material-icons">
                                        search
                                </i>
                                </div>
                            </div>
                        </div>
                        <p className="_counter">
                            {countPatients} {countPatients === 1? t('participant') : t('participants')}
                        </p>
                        {
                            currentUser.userRoles.programsEnroll.edit?
                                <div className="enrollAction">
                                    <ButtonIcon handleClick={enrollParticipant}>
                                        <span className="material-icons">
                                            add
                                    </span>
                                    </ButtonIcon>
                                    <p>{t('enroll-participant')}</p>
                                </div> : <div className="empty"></div>
                        }
                    </div>
                </div>
                <div className="patientsList scrollBar">
                    <div className="headers">
                        <div>
                            {
                                currentUser.userRoles.userPatients.edit? <Checkbox bool={selectAll} onChange={onSelectAll}/> : ''
                            }
                        </div>
                        <div></div>
                        <div className="selectable sortable">
                            <p onClick={sortList('name')}>
                                {t('users:name')}
                            </p>
                            <div className="_updown" onClick={sortList('name')}>
                                <span className="_up material-icons">expand_less</span>
                                <span className="_down material-icons">expand_more</span>
                            </div>
                        </div>
                        <div className="selectable sortable centered">
                            <p onClick={sortList('age')}>
                                {t('users:age')}
                            </p>
                            <div className="_updown" onClick={sortList('age')}>
                                <span className="_up material-icons">expand_less</span>
                                <span className="_down material-icons">expand_more</span>
                            </div>
                        </div>
                        <div className="selectable">
                            <p>
                                {t('users:gender')}
                        </p>
                            <div className="_updown"></div>
                        </div>
                        <div className="selectable">
                            <p>
                                {t('users:date-of-birth')}
                        </p>
                            <div className="_updown"></div>
                        </div>
                        <div className="selectable">
                            <p>
                                {t('users:telephone')}
                        </p>
                            <div className="_updown"></div>
                        </div>
                        <div className="selectable centered">
                            <p>
                                {t('devices:kit-id')}
                            </p>
                            <div className="_updown"></div>
                        </div>
                        <div className="selectable">
                            <p>
                        </p>
                            <div className="_updown"></div>
                        </div>
                        <div>
                            {
                                currentUser.userRoles.userPatients.edit && selectedParticipants.length?
                                    <ItemMenu actionWillRedirect={false} fixed={true}>
                                        <>
                                            <div className="_option" onClick={onAddParticipants}>
                                                <p>{selectedParticipants.length === 1? t('teams:assign-unassign-single-participant'): t('teams:assign-unassign-multiple-participants')}</p>
                                            </div>
                                            {
                                                enableDownloadBioReports()?
                                                    <div className={`_option`} onClick={() => onDownloadBioReports(true)}><p>Generate BioReport(s)</p></div> : ''
                                            }
                                        </>
                                    </ItemMenu> : ''
                            }
                        </div>
                    </div>
                    {
                        newPatientsFiltered.map((patient) => (
                            <ProgramBasePatient currentOrg={currentOrg} currentUser={currentUser} key={patient._id} hideMenu={!currentUser.userRoles.devicesAssign.edit} requestAssignKit={assignKitToSelected(patient)} patient={patient} selectedPatients={selectedParticipants} selected={selectedPatient && patient._id === selectedPatient._id} onSelected={onSelect} onAdd={onAddParticipant} onChange={onChange} onDownloadBioReports={() => onDownloadBioReports(false)} patientsBioDevices={patientsBioDevices} />
                        ))
                    }
                    {
                        oldPatientsFiltered.map((patient) => (
                            <ProgramBasePatient currentOrg={currentOrg} currentUser={currentUser} key={patient._id} hideMenu={!currentUser.userRoles.devicesAssign.edit} requestAssignKit={assignKitToSelected(patient)} patient={patient} selectedPatients={selectedParticipants} selected={selectedPatient && patient._id === selectedPatient._id} onSelected={onSelect} onAdd={onAddParticipant} onChange={onChange} onDownloadBioReports={() => onDownloadBioReports(false)} patientsBioDevices={patientsBioDevices} />
                        ))
                    }
                    { loadData? <SpinnerLoader/>: '' }
                </div>
                {
                    program ?
                        <ProgramsBaseEnrollPatientDialog currentOrg={currentOrg} openModal={requestEnroll} closeModal={closeEnrollModal} program={program} />
                        : ''
                }
                <KitAssignDialog currentOrg={currentOrg} patientToAssign={assigneePatient} askToClose={closeAssignKit} />
                {
                    <ParticipantCliniciansDialog program={program} item={selectedParticipant} items={selectedParticipants} openModal={openParticipantsCliniciansModal} closeModal={closeParticipantsCliniciansModal} />
                }
                {
                    <ProgramGenerateZipReportsDialog currentUser={currentUser} program={program} multipleReports={multipleReports} selectedParticipants={selectedParticipants} selectedPatient={selectedPatient} openModal={openZipModal} closeModal={onCloseGenerateZipModal} />
                }
                {
                    <ProgramDownloadZipReportsDialog link={reportLink} program={program} openModal={openDownloadModal} closeModal={onCloseDownloadZipModal}/>
                }
            </div>
        )
    }

    return (renderProgramEnrolled())
}

export default ProgramsBaseEnrolled;
